import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Title, Loader, ReloadIconButton } from '../../elements';
import { useConfirmation } from '../../hooks/ConfirmationService';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE_MIDDLE, LoaderSize } from '../../constants/constants';
import { notifications, extractApiErrorMessage } from '../../utils';
import {
  Chip,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { colors } from '../../utils';
import { V4_ICON_BUTTON_SMALL, V4_TABLE } from '../../theme/v4Compat';
import { useTranslation } from 'react-i18next';
import { ApiService } from '../../hooks';
import { HealthSystemDomainDocumentsResultFarmDocumentViewModel } from '../../api/HealthSystemApi';
import saveAs from 'file-saver';
import { usePermission } from '../../hooks/usePermission';
import { Permission } from '../../constants/permissions';

/** The query parameter an export notification points at a document with. */
export const FOCUS_DOCUMENT_PARAM = 'documentId';

const FarmDocuments: React.FC<any> = () => {
  const styles = sxStyles(useTheme());
  const { t } = useTranslation();
  const confirmDialog = useConfirmation();
  const { hasPermission } = usePermission();

  const apiService = new ApiService();

  const [searchParams, setSearchParams] = useSearchParams();

  const [docs, setDocs] = React.useState<HealthSystemDomainDocumentsResultFarmDocumentViewModel[]>([]);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);
  const [highlightedId, setHighlightedId] = React.useState<string | null>(null);

  // Consumed by the first load and then dropped, so a manual page change or a reload does not drag the
  // notification's document back into view.
  const pendingFocusRef = React.useRef<string | null>(searchParams.get(FOCUS_DOCUMENT_PARAM));
  const highlightedRowRef = React.useRef<HTMLTableRowElement | null>(null);

  const canDelete = hasPermission(Permission.VIEW_FARM_INFO);

  const loadFarmDocuments = async () => {
    setIsBusy(true);

    const searchPage = page === 0 ? 1 : page + 1;
    const focusDocumentId = pendingFocusRef.current;

    apiService.api.documents
      .documentsSearchDocuments({
        Page: searchPage,
        Size: rowsPerPage,
        ...(focusDocumentId ? { FocusDocumentId: focusDocumentId } : {})
      })
      .then((result) => {
        setDocs(result.data.data);
        setTotal(result.data.total);

        if (focusDocumentId) {
          // The focused document decides the page, so the table follows the response rather than the other
          // way round - the notification knew an id, not a page.
          setPage(result.data.page - 1);
          setHighlightedId(focusDocumentId);
        }
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        pendingFocusRef.current = null;
        setIsBusy(false);
        setLoaded(true);
      });
  };

  React.useEffect(() => {
    if (loaded === false) {
      const loadData = async () => loadFarmDocuments();
      loadData();
    }
  }, [loaded]);

  // A second notification followed while this tab is already open navigates to the same route, so nothing
  // remounts and the id has to be picked up from the changed parameter instead.
  React.useEffect(() => {
    const focusDocumentId = searchParams.get(FOCUS_DOCUMENT_PARAM);

    if (focusDocumentId && pendingFocusRef.current !== focusDocumentId) {
      pendingFocusRef.current = focusDocumentId;
      setLoaded(false);
    }
  }, [searchParams]);

  React.useEffect(() => {
    if (!highlightedId) {
      return;
    }

    // Dropped once it has been acted on, so the highlight does not come back when the tab is reopened.
    if (searchParams.has(FOCUS_DOCUMENT_PARAM)) {
      searchParams.delete(FOCUS_DOCUMENT_PARAM);
      setSearchParams(searchParams, { replace: true });
    }

    highlightedRowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [highlightedId, docs]);

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE_MIDDLE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setHighlightedId(null);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setHighlightedId(null);
    setLoaded(false);
  };
  // * pagination

  const handleRemoveDocument = (doc: HealthSystemDomainDocumentsResultFarmDocumentViewModel) => {
    confirmDialog({
      dialogId: 'delete-doc-dialog',
      title: t('f-document-del'),
      message: t('f-document-del-msg', { file: doc.fileName }),
      alertSeverity: 'warning'
    }).then(() => {
      apiService.api.documents
        .documentsDeleteFarmDocument({ id: doc.id })
        .then(() => {
          notifications.success(t('f-document-del-ok'));
          setLoaded(false);
        })
        .catch((e) => {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        });
    });
  };

  const handleDownloadDocument = (doc: HealthSystemDomainDocumentsResultFarmDocumentViewModel) => {
    notifications.success(t('f-document-down-ok'));

    apiService.api.documents
      .documentsDownloadExportDocument({ Id: doc.id })
      .then(async (result) => {
        const exportDocument = await result.blob();
        saveAs(exportDocument, doc.fileName);
      })
      .catch((e: unknown) => notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') })));
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={12}>
        {/* title & spinner */}
        <Grid container direction="row" sx={styles.paperHeader}>
          <Grid item container md={6} xs={10} spacing={1}>
            <Grid item>
              <Icon fontSize="large" color="primary">
                {'description'}
              </Icon>
            </Grid>
            <Grid item>
              <Title>{t('f-documents')}</Title>
            </Grid>
            <Grid item>
              <Chip label={total} variant="outlined" />
            </Grid>
          </Grid>

          <Grid item container sx={styles.justifyRight} md={6} xs={10}>
            <Grid item>
              <ReloadIconButton action={() => setLoaded(false)} />
            </Grid>
          </Grid>
        </Grid>

        <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

        {/* data table */}
        {loaded && (
          <Grid container spacing={3}>
            <Grid item md={12}>
              <Paper elevation={1}>
                <Table size="small" sx={{ ...V4_TABLE, ...styles.resultsTable }}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">{t('m-global-filename')}</TableCell>
                      <TableCell align="center">{t('f-com-created')}</TableCell>
                      <TableCell align="center">{t('f-document-created-by')}</TableCell>
                      <TableCell align="center">{t('f-document-type')}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {docs.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        ref={row.id === highlightedId ? highlightedRowRef : undefined}
                        sx={row.id === highlightedId ? styles.highlightedRow : undefined}
                      >
                        <TableCell align="center">{row.fileName}</TableCell>
                        <TableCell align="center">{new Date(row.created).toLocaleString()}</TableCell>
                        <TableCell align="center">{row.createdBy}</TableCell>
                        <TableCell align="center">{t('f-document-type-export')}</TableCell>
                        <TableCell align="right">
                          {canDelete && (
                            <Tooltip arrow title={t('f-document-del')}>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleRemoveDocument(row)}
                                sx={V4_ICON_BUTTON_SMALL}
                              >
                                <Icon>{'delete_outline'}</Icon>
                              </IconButton>
                            </Tooltip>
                          )}

                          <Tooltip arrow title={t('m-global-download')}>
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleDownloadDocument(row)}
                              sx={V4_ICON_BUTTON_SMALL}
                            >
                              <Icon>{'cloud_download'}</Icon>
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>

                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                        colSpan={6}
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default FarmDocuments;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column'
    },
    paperHeader: {
      justifyContent: 'space-between'
    },
    justifyRight: {
      justifyContent: 'flex-end',
      alignItems: 'center'
    },
    withMarginLeft: {
      marginLeft: theme.spacing(1)
    },
    tableInfoChip: {
      marginLeft: theme.spacing(2),
      background: colors.defaultGrey
    },
    resultsTable: {
      marginTop: theme.spacing(2)
    },
    // The row an export notification pointed at. The left border carries the meaning on its own, so the
    // highlight survives the hover tint the table paints over the background.
    highlightedRow: {
      backgroundColor: theme.palette.action.selected,
      borderLeft: `${theme.spacing(0.5)} solid ${theme.palette.primary.main}`
    }
  }) as const;
